import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Label,
  FormGroup,
  Spinner,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import { toast } from "react-toastify";
import { DatosSesion } from "../../../../Components/Common/DatosSesion";
import { getProveedoresDashboard, getUsuarios, getFarmacias } from "../../../../helpers/backend_helper";

/**
 * Componente para la barra de filtros de solicitudes
 * @param {Object} props - Propiedades del componente
 * @returns {JSX.Element} - Componente de barra de filtros
 */
const FilterBar = ({
  fechaInicio,
  fechaFin,
  onFechaInicioChange,
  onFechaFinChange,
  onFilterApply,
  onFilterReset,
  onGenerateRoute,
  solicitudesCount = 0,
  loading = false,
  rutaActiva = null,
  codigoProveedorUsuario = null, // Código del proveedor del usuario logueado (para Farmacia)
}) => {
  const [fechaInicioLocal, setFechaInicioLocal] = useState(fechaInicio);
  const [fechaFinLocal, setFechaFinLocal] = useState(fechaFin);

  // Obtener perfil del usuario
  const perfil = DatosSesion("perfil") || "";
  const esAdministrador = perfil === "Administrador";
  const esFarmacia = perfil === "Farmacia";
  const esDelivery = perfil === "Delivery";

  // Estados para filtros de proveedor y motorizado
  const [proveedores, setProveedores] = useState([]);
  const [farmacias, setFarmacias] = useState([]); // Para mapear codigo_myh -> id
  const [motorizados, setMotorizados] = useState([]);
  const [todosLosUsuarios, setTodosLosUsuarios] = useState([]); // Cache de usuarios
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState("");
  const [motorizadoSeleccionado, setMotorizadoSeleccionado] = useState("");
  const [cargandoProveedores, setCargandoProveedores] = useState(false);
  const [cargandoMotorizados, setCargandoMotorizados] = useState(false);

  // Actualizar fechas locales cuando cambian las props
  useEffect(() => {
    setFechaInicioLocal(fechaInicio);
    setFechaFinLocal(fechaFin);
  }, [fechaInicio, fechaFin]);

  // Cargar proveedores y farmacias si es Administrador
  useEffect(() => {
    if (esAdministrador) {
      setCargandoProveedores(true);
      // Cargar proveedores y farmacias en paralelo
      Promise.all([getProveedoresDashboard(), getFarmacias()])
        .then(([proveedoresRes, farmaciasRes]) => {
          if (proveedoresRes.data?.data) {
            setProveedores(proveedoresRes.data.data);
          }
          // Farmacias: response.data.data[0] contiene el array
          const farmaciasData = farmaciasRes.data?.data;
          if (Array.isArray(farmaciasData) && Array.isArray(farmaciasData[0])) {
            setFarmacias(farmaciasData[0]);
            //console.log("🏪 Farmacias cargadas:", farmaciasData[0].length);
          }
        })
        .catch((error) => {
          console.error("Error al cargar proveedores/farmacias:", error);
        })
        .finally(() => {
          setCargandoProveedores(false);
        });
    }
  }, [esAdministrador]);

  // Cargar todos los usuarios (motorizados)
  useEffect(() => {
    if (esAdministrador || esFarmacia) {
      setCargandoMotorizados(true);
      getUsuarios()
        .then((response) => {
          // La respuesta tiene estructura { data: { data: [[...usuarios], count], message, status_code } }
          const dataArray = response.data?.data;
          const usuarios = Array.isArray(dataArray) && Array.isArray(dataArray[0]) 
            ? dataArray[0] 
            : [];
          
          //console.log("👥 Usuarios extraídos:", usuarios.length);
          
          // Guardar todos los usuarios Delivery activos en cache
          const usuariosDelivery = usuarios.filter(
            (u) => u.perfil === "Delivery" && u.activo
          );
          
          //console.log("🏍️ Usuarios Delivery activos:", usuariosDelivery.length);
          setTodosLosUsuarios(usuariosDelivery);

          // Si es Farmacia, filtrar solo los motorizados asociados a su farmacia
          if (esFarmacia) {
            const farmaciaIdUsuario = DatosSesion("farmacia_id");
            //console.log("🏪 Farmacia del usuario:", farmaciaIdUsuario);
            if (farmaciaIdUsuario) {
              const filtrados = usuariosDelivery.filter(
                (u) => u.farmacia === farmaciaIdUsuario
              );
              //console.log("🏍️ Delivery de esta farmacia:", filtrados.length);
              setMotorizados(filtrados);
            } else {
              setMotorizados(usuariosDelivery);
            }
          } else {
            // Admin: mostrar todos inicialmente
            setMotorizados(usuariosDelivery);
          }
        })
        .catch((error) => {
          console.error("Error al cargar motorizados:", error);
        })
        .finally(() => {
          setCargandoMotorizados(false);
        });
    }
  }, [esAdministrador, esFarmacia]);

  // Cuando cambia el proveedor seleccionado (Admin), filtrar motorizados usando cache
  useEffect(() => {
    if (!esAdministrador) return;
    
    if (proveedorSeleccionado && farmacias.length > 0 && todosLosUsuarios.length > 0) {
      // Buscar las farmacias que tienen el codigo_myh del proveedor seleccionado
      const farmaciaIdsDelProveedor = farmacias
        .filter((f) => f.codigo_myh === proveedorSeleccionado)
        .map((f) => f.id);
      
      //console.log("🔍 Proveedor seleccionado:", proveedorSeleccionado);
      //console.log("🏪 Farmacias del proveedor:", farmaciaIdsDelProveedor);
      
      // Filtrar motorizados que pertenecen a esas farmacias
      const motorizadosFiltrados = todosLosUsuarios.filter(
        (u) => farmaciaIdsDelProveedor.includes(u.farmacia)
      );
      
      //console.log("🏍️ Motorizados del proveedor:", motorizadosFiltrados.length);
      setMotorizados(motorizadosFiltrados);
      setMotorizadoSeleccionado(""); // Reset motorizado al cambiar proveedor
    } else if (!proveedorSeleccionado && todosLosUsuarios.length > 0) {
      // Sin proveedor seleccionado, mostrar todos
      setMotorizados(todosLosUsuarios);
      setMotorizadoSeleccionado("");
    }
  }, [esAdministrador, proveedorSeleccionado, farmacias, todosLosUsuarios]);

  // Manejar cambios en las fechas
  const handleFechaInicioChange = (selectedDates) => {
    const newDate = selectedDates[0];
    setFechaInicioLocal(newDate);
    onFechaInicioChange && onFechaInicioChange(newDate);
  };

  const handleFechaFinChange = (selectedDates) => {
    const newDate = selectedDates[0];
    setFechaFinLocal(newDate);
    onFechaFinChange && onFechaFinChange(newDate);
  };

  // Aplicar filtros
  const handleApplyFilter = () => {
    // Asegurarse de que las fechas sean serializables (no objetos Date)
    const formatDate = (date) => {
      if (!date) return null;

      // Si ya es un string, verificar si tiene formato ISO
      if (typeof date === "string") {
        // Si ya tiene formato YYYY-MM-DD, devolverlo tal cual
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return date;
        }
        // Intentar convertir el string a Date
        try {
          const dateObj = new Date(date);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toISOString().split("T")[0];
          }
        } catch (e) {
          console.error("Error al convertir fecha:", e);
        }
      }

      // Si es un objeto Date válido
      if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString().split("T")[0];
      }

      // Si no se pudo convertir, devolver null
      console.warn("No se pudo convertir la fecha:", date);
      return null;
    };

    onFilterApply &&
      onFilterApply({
        fechaInicio: formatDate(fechaInicioLocal),
        fechaFin: formatDate(fechaFinLocal),
        codigoProveedor: proveedorSeleccionado || null,
        motorizadoId: motorizadoSeleccionado || null,
      });
  };

  // Resetear filtros
  const handleResetFilter = () => {
    setFechaInicioLocal(null);
    setFechaFinLocal(null);
    setProveedorSeleccionado("");
    setMotorizadoSeleccionado("");
    onFilterReset && onFilterReset();
  };

  // Generar ruta
  const handleGenerateRoute = () => {
    if (!rutaActiva) {
      toast.info("Generando ruta de entrega optimizada...", {
        autoClose: 3000,
        position: "top-right",
      });
    }
    onGenerateRoute && onGenerateRoute();
  };

  // Obtener la fecha actual para el filtro predeterminado
  const today = new Date();
  const todayFormatted = today.toISOString().split("T")[0];

  return (
    <Card className="mb-4 border-0 shadow-sm">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Filtrar Solicitudes</h5>
          <span className="badge bg-primary rounded-pill">
            {solicitudesCount} solicitudes
          </span>
        </div>

        <Row className="g-3">
          <Col md={6}>
            <FormGroup>
              <Label for="fechaInicio" className="form-label">
                Fecha Inicio
              </Label>
              <Flatpickr
                className="form-control"
                placeholder="Seleccione fecha inicio"
                options={{
                  dateFormat: "Y-m-d",
                  locale: Spanish,
                  defaultDate: todayFormatted,
                }}
                value={fechaInicioLocal}
                onChange={handleFechaInicioChange}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="fechaFin" className="form-label">
                Fecha Fin
              </Label>
              <Flatpickr
                className="form-control"
                placeholder="Seleccione fecha fin"
                options={{
                  dateFormat: "Y-m-d",
                  locale: Spanish,
                  defaultDate: todayFormatted,
                }}
                value={fechaFinLocal}
                onChange={handleFechaFinChange}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Filtro de Farmacia - Solo para Administrador */}
        {esAdministrador && (
          <Row className="g-3 mt-2">
            <Col md={12}>
              <FormGroup>
                <Label for="farmacia" className="form-label">
                  <i className="ri-store-2-line me-1"></i>
                  Farmacia
                </Label>
                {cargandoProveedores ? (
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" color="primary" className="me-2" />
                    <span className="text-muted">Cargando farmacias...</span>
                  </div>
                ) : (
                  <Input
                    type="select"
                    id="farmacia"
                    value={proveedorSeleccionado}
                    onChange={(e) => {
                      setProveedorSeleccionado(e.target.value);
                      setMotorizadoSeleccionado(""); // Resetear motorizado al cambiar farmacia
                    }}
                  >
                    <option value="">Todas las farmacias</option>
                    {proveedores.map((prov) => (
                      <option key={prov.codigo} value={prov.codigo}>
                        {prov.nombre}
                      </option>
                    ))}
                  </Input>
                )}
              </FormGroup>
            </Col>
          </Row>
        )}

        {/* Filtro de Motorizado - Para Administrador y Farmacia */}
        {(esAdministrador || esFarmacia) && !esDelivery && (
          <Row className="g-3 mt-2">
            <Col md={12}>
              <FormGroup>
                <Label for="motorizado" className="form-label">
                  <i className="ri-motorbike-line me-1"></i>
                  Motorizado
                </Label>
                {cargandoMotorizados ? (
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" color="primary" className="me-2" />
                    <span className="text-muted">Cargando motorizados...</span>
                  </div>
                ) : (
                  <Input
                    type="select"
                    id="motorizado"
                    value={motorizadoSeleccionado}
                    onChange={(e) => setMotorizadoSeleccionado(e.target.value)}
                  >
                    <option value="">Todos los motorizados</option>
                    {motorizados.map((mot) => (
                      <option key={mot.id || mot._id} value={mot.id || mot._id}>
                        {mot.nombre} {mot.apellido}
                      </option>
                    ))}
                  </Input>
                )}
              </FormGroup>
            </Col>
          </Row>
        )}

        <div className="d-flex gap-2 mt-3">
          <Button color="light" className="w-100" onClick={handleResetFilter}>
            <i className="ri-refresh-line me-1"></i>
            Limpiar
          </Button>
          <Button color="primary" className="w-100" onClick={handleApplyFilter}>
            <i className="ri-filter-line me-1"></i>
            Aplicar Filtros
          </Button>
        </div>

        {rutaActiva === null && (
          <div className="mt-4">
            <Button
              color="success"
              className="w-100"
              onClick={handleGenerateRoute}
              disabled={solicitudesCount === 0 || loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Generando ruta optimizada...
                </>
              ) : rutaActiva ? (
                <>
                  <i className="ri-route-line me-1"></i>
                  Ver ruta activa
                </>
              ) : (
                <>
                  <i className="ri-route-line me-1"></i>
                  Generar Ruta de Entrega
                </>
              )}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default FilterBar;
